import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const fieldClass = (readOnly) =>
  `h-9 w-full rounded-lg border px-3 text-sm text-zinc-900 outline-none transition ${
    readOnly
      ? "border-zinc-200 bg-zinc-50"
      : "border-zinc-200 bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
  }`;

const textareaClass = (readOnly) =>
  `w-full resize-y rounded-lg border px-3 py-2 text-sm leading-6 text-zinc-900 outline-none transition ${
    readOnly
      ? "border-zinc-200 bg-zinc-50"
      : "border-zinc-200 bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
  }`;

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
      {children}
    </label>
  );
}

function Card({ title, description, children }) {
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
        {description ? <p className="text-xs text-zinc-500">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * Presentational property form. The screen owns the state; this component
 * renders Address / Listing / Schedule cards in read-only or edit mode.
 */
export default function PropertyDetailsComponent({
  address,
  credentials,
  checkInTime,
  checkOutTime,
  readOnly,
  onAddressChange,
  onCredentialsChange,
  onCheckInChange,
  onCheckOutChange,
}) {
  const handleAddress = (event) => onAddressChange({ ...address, [event.target.name]: event.target.value });
  const handleCredentials = (event) => onCredentialsChange({ ...credentials, [event.target.name]: event.target.value });

  return (
    <div className="grid grid-cols-1 gap-3 laptop:grid-cols-12">
      <div className="space-y-3 laptop:col-span-7">
        <Card title="Address" description="Where the concierge delivers.">
          <div className="grid grid-cols-1 gap-3 laptop:grid-cols-2">
            <Field label="Line 1">
              <input name="line1" value={address.line1 || ""} onChange={handleAddress} readOnly={readOnly} className={fieldClass(readOnly)} />
            </Field>
            <Field label="Line 2">
              <input name="line2" value={address.line2 || ""} onChange={handleAddress} readOnly={readOnly} className={fieldClass(readOnly)} placeholder="Unit, suite (optional)" />
            </Field>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 laptop:grid-cols-3">
            <Field label="City">
              <input name="city" value={address.city || ""} onChange={handleAddress} readOnly={readOnly} className={fieldClass(readOnly)} />
            </Field>
            <Field label="State">
              <input name="state" value={address.state || ""} onChange={handleAddress} readOnly={readOnly} className={fieldClass(readOnly)} />
            </Field>
            <Field label="ZIP">
              <input name="zipCode" value={address.zipCode || ""} onChange={handleAddress} readOnly={readOnly} className={fieldClass(readOnly)} />
            </Field>
          </div>
        </Card>

        <Card title="Listing" description="Shown to guests and used by the concierge team.">
          <Field label="Listing title">
            <input name="listingTitle" value={credentials.listingTitle || ""} onChange={handleCredentials} readOnly={readOnly} className={fieldClass(readOnly)} />
          </Field>
          <Field label="Entry instructions / description" className="mt-3">
            <textarea
              name="description"
              rows={4}
              value={credentials.description || ""}
              onChange={handleCredentials}
              readOnly={readOnly}
              placeholder="How the concierge gets in, where to leave groceries, parking notes…"
              className={textareaClass(readOnly)}
            />
          </Field>
          <Field label="Key location" className="mt-3">
            <textarea
              name="keyInfo"
              rows={2}
              value={credentials.keyInfo || ""}
              onChange={handleCredentials}
              readOnly={readOnly}
              placeholder="Lockbox code location, key safe, smart lock…"
              className={textareaClass(readOnly)}
            />
          </Field>
        </Card>
      </div>

      <div className="laptop:col-span-5">
        <Card title="Schedule" description="Default check-in and check-out times.">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Check-in">
              <DatePicker
                selected={checkInTime}
                onChange={onCheckInChange}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="h:mm aa"
                placeholderText="—"
                readOnly={readOnly}
                className={fieldClass(readOnly)}
                wrapperClassName="w-full"
              />
            </Field>
            <Field label="Check-out">
              <DatePicker
                selected={checkOutTime}
                onChange={onCheckOutChange}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="h:mm aa"
                placeholderText="—"
                readOnly={readOnly}
                className={fieldClass(readOnly)}
                wrapperClassName="w-full"
              />
            </Field>
          </div>
        </Card>
      </div>
    </div>
  );
}
